import type { ManagerPerm } from '../types'
import { ReactNode } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { Spinner } from './Spinner'

function Loading() {
  // Kendi konumunu kuran sarmalayıcıya gerek yok: full kipi ekranı
  // kendisi kaplıyor.
  return <Spinner label="Yükleniyor…" full />
}

/**
 * Where a guard sends someone, under the institution in the address.
 *
 * Built from the route param rather than a relative path: these guards render
 * at several depths ("/:tenant", "/:tenant/ayarlar"), and ".." would mean a
 * different place in each.
 */
function useTenantPath(): (to: string) => string {
  const { tenant } = useParams()
  return (to: string) => `/${tenant}${to === '/' ? '' : to}`
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isKitchen, isKiosk, loading } = useAuth()
  const path = useTenantPath()

  if (loading) return <Loading />
  if (!isAuthenticated) return <Navigate to="/giris" replace />
  // Mutfak personelinin yeri kasa değil, mutfak ekranı. Öbür korumalar "/"a
  // gönderdiği için onlar da buraya, oradan mutfağa düşüyor.
  if (isKitchen) return <Navigate to={path('/mutfak')} replace />
  // Kiosk tableti yalnız müşteri sipariş ekranında (30 Eki 2026).
  if (isKiosk) return <Navigate to={path('/kiosk')} replace />

  return <>{children}</>
}

/**
 * Reports and settings are owner-only; cashiers only get the till.
 * Müdür (3 Kas 2026): `perm` izni verilmişse o da girer; 'settings-page'
 * Ayarlar sayfası, ayar ya da personel izniyle açılır.
 */
export function OwnerRoute({ children, perm }: { children: ReactNode; perm?: ManagerPerm | 'settings-page' }) {
  const { isAuthenticated, isOwner, can, canOpenSettings, loading } = useAuth()
  const path = useTenantPath()

  if (loading) return <Loading />
  if (!isAuthenticated) return <Navigate to="/giris" replace />
  const allowed = isOwner || (perm === 'settings-page' ? canOpenSettings : perm ? can(perm) : false)
  if (!allowed) return <Navigate to={path('/')} replace />

  return <>{children}</>
}

/**
 * The menu, open to the till as well as the owner. At a restaurant the person at
 * the counter is the one who finds out a tray ran out or a price is wrong, and
 * sending them to fetch the owner mid-service costs more than it protects.
 * Waiters are still kept out, they take orders, they do not set prices.
 */
export function MenuRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, canSettle, loading } = useAuth()
  const path = useTenantPath()

  if (loading) return <Loading />
  if (!isAuthenticated) return <Navigate to="/giris" replace />
  if (!canSettle) return <Navigate to={path('/')} replace />

  return <>{children}</>
}

/**
 * A screen belonging to an optional feature.
 *
 * Refused where the institution has not been given it, so the address bar is
 * not a way around a flag that is off. Sent to "/" rather than shown a
 * message: to this institution the screen does not exist, and explaining that
 * it exists elsewhere would only raise a question nobody there can act on.
 */
export function CariRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, canSettle, hasFeature, loading, settingsLoading } = useAuth()
  const path = useTenantPath()

  if (loading) return <Loading />
  if (!isAuthenticated) return <Navigate to="/giris" replace />
  // Bayraklar gelmeden bakılırsa cari kapalı görünüyor ve yenileyen kişi
  // ana sayfaya düşüyor.
  if (settingsLoading) return <Loading />
  if (!hasFeature('cari')) return <Navigate to={path('/')} replace />
  // Money owed is the till's business, not a waiter's, the same line drawn
  // around taking payment at all.
  if (!canSettle) return <Navigate to={path('/')} replace />

  return <>{children}</>
}

/**
 * The system administrator's screen, opening institutions, not running one.
 *
 * Only waits for the session: SuperadminPage decides for itself what a
 * visitor who is not signed in (sent to "/giris") or not the system
 * administrator (refused) sees.
 */
export function SuperadminRoute({ children }: { children: ReactNode }) {
  const { loading } = useAuth()
  if (loading) return <Loading />
  return <>{children}</>
}

/**
 * Sipariş Takip, behind its feature and the waiter's own permission. Waits for the institution's
 * settings so a reload on /takip does not bounce while the flags load.
 */
export function TrackRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, canTrack, loading, settingsLoading } = useAuth()
  const path = useTenantPath()

  if (loading) return <Loading />
  if (!isAuthenticated) return <Navigate to="/giris" replace />
  if (settingsLoading) return <Loading />
  if (!canTrack) return <Navigate to={path('/')} replace />

  return <>{children}</>
}

/**
 * Mutfak ekranı: mutfak personeli, patron ve kasa. Garson yok, onun ekranı
 * Sipariş takip.
 */
export function KitchenRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isKitchen, isOwner, loading } = useAuth()
  const path = useTenantPath()

  if (loading) return <Loading />
  if (!isAuthenticated) return <Navigate to="/giris" replace />
  // Kasiyer değil (kullanıcı, 21 Eki 2026): mutfak personeli ve patron.
  if (!isKitchen && !isOwner) return <Navigate to={path('/')} replace />

  return <>{children}</>
}

/** Kiosk ekranı: kiosk tableti ve önizlemek isteyen patron. */
export function KioskRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isKiosk, isOwner, loading } = useAuth()
  const path = useTenantPath()

  if (loading) return <Loading />
  if (!isAuthenticated) return <Navigate to="/giris" replace />
  if (!isKiosk && !isOwner) return <Navigate to={path('/')} replace />

  return <>{children}</>
}
